const DEFAULT_TIMEOUT_MS = 15000;

class ApiError extends Error {
  constructor(message, { status, code, requestId, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

/** Prefer FastAPI `detail`, then common API error shapes. */
function messageFromErrorBody(data, status) {
  if (!data || typeof data !== 'object') {
    return `Request failed (${status})`;
  }

  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) {
      const items = Array.isArray(detail.items) ? detail.items.filter(Boolean) : [];
      return items.length ? `${detail.message}: ${items.join(', ')}` : detail.message;
    }
    // Pydantic / FastAPI validation errors: detail is an array of { loc, msg, type }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (first && typeof first.msg === 'string') {
        const loc = Array.isArray(first.loc) ? first.loc.filter((p) => p !== 'body').join('.') : '';
        return loc ? `${loc}: ${first.msg}` : first.msg;
      }
    }
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }
  if (Array.isArray(data.errors) && data.errors[0]?.msg) {
    return data.errors[0].msg;
  }
  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  return `Request failed (${status})`;
}

export async function apiRequest(path, { method = 'GET', headers, body, timeoutMs } = {}) {
  const controller = new AbortController();
  const ms = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;
  const timer = window.setTimeout(() => controller.abort(), ms);

  try {
    const res = await fetch(path, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : null),
        ...(headers || null),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    const data = await safeJson(res);
    const requestId =
      (data && typeof data.requestId === 'string' && data.requestId) ||
      res.headers.get('x-request-id') ||
      '';

    if (!res.ok) {
      const message = messageFromErrorBody(data, res.status);
      throw new ApiError(message, { status: res.status, requestId, details: data });
    }

    return { data, requestId, status: res.status };
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new ApiError('Request timed out. Please try again.', { code: 'TIMEOUT' });
    }
    if (err instanceof ApiError) throw err;
    throw new ApiError('Network error. Please check your connection and try again.', {
      code: 'NETWORK',
    });
  } finally {
    window.clearTimeout(timer);
  }
}

export const api = {
  get: (path, options) => apiRequest(path, { ...(options || {}), method: 'GET' }),
  post: (path, body, options) => apiRequest(path, { ...(options || {}), method: 'POST', body }),
  put: (path, body, options) => apiRequest(path, { ...(options || {}), method: 'PUT', body }),
  patch: (path, body, options) => apiRequest(path, { ...(options || {}), method: 'PATCH', body }),
  del: (path, options) => apiRequest(path, { ...(options || {}), method: 'DELETE' }),
};

export { ApiError };
